/* @flow */
import idx from 'idx';
import React from 'react';
import { graphql } from 'react-apollo';
import PhoneCallPreferenceSection from 'settings/components/PhoneCallPreferenceSection';
import GET_PERMISSION_QUERY from '../queries/PhoneCallPreferencePermissionQuery.gql';
import UPDATE_PREFERENCE_MUTATION from '../queries/UpdatePhoneCallPreferenceMutation.gql';
import GET_DEVICES_QUERY from 'settings/queries/devicesQuery.gql';
import update from 'immutability-helper';

import type { PhoneCallPreferencePermission } from '../queries/__generated__/PhoneCallPreferencePermission';
import type { updatePhoneCallPreferenceVariables } from '../queries/__generated__/updatePhoneCallPreference';
import type { Devices, Devices_devices } from '../queries/__generated__/Devices.js';

type Props = {
  smsSupported: boolean,
  mutate: (input: { variables: updatePhoneCallPreferenceVariables }) => Promise<*>,
  data: ReactApolloData<PhoneCallPreferencePermission>,
};

type State = {
  persisted: boolean,
  persistError: ?Error,
};

function buildOptimisticMutationResponse(props: Props, value: string) {
  const uuid = idx(props.data, _ => _.me.uuid);

  if (!uuid) {
    return undefined;
  }

  return {
    __typename: 'Mutation',
    updateUserPrefs: {
      __typename: 'UpdateUserPrefsPayload',
      user: {
        __typename: 'User',
        uuid,
        preferences: {
          __typename: 'UserPreferences',
          receive_phone_calls: value,
        },
      },
    },
  };
}

function setPreferredVoiceDevice(devices: $ReadOnlyArray<Devices_devices>) {
  const voiceDevices = devices.filter(device => device.type === 'sms' || device.type === 'landline');

  if (voiceDevices.length === 1) {
    const preferredDeviceId = voiceDevices[0].id;
    return devices.map<Devices_devices>(item => update(item, {
      is_preferred_voice_calls_device: {
        $set: item.id === preferredDeviceId,
      },
    }))
  }

  return devices;
}

function updatePreferredDeviceOnResponse(store) {
  const queryData: Devices = store.readQuery({ query: GET_DEVICES_QUERY });

  const updatedQueryData = update(queryData, {
    devices: {
      $apply: setPreferredVoiceDevice,
    },
  });

  store.writeQuery({ query: GET_DEVICES_QUERY, data: updatedQueryData })
}

class PhoneCallPreferenceSectionContainer extends React.Component<Props, State> {
  state: State = {
    persisted: true,
    persistError: null,
  };

  listen = (mutator: () => Promise<*>): void => {
    this.setState({ persisted: false, persistError: null });

    mutator()
      .then(() => this.setState({ persisted: true }))
      .catch(e => this.setState({ persistError: e }));
  };

  enable = (): void => {
    this.listen(() =>
      this.props.mutate({
        variables: { value: 'enabled' },
        optimisticResponse: buildOptimisticMutationResponse(this.props, 'enabled'),
        update: updatePreferredDeviceOnResponse,
        refetchQueries: [{ query: GET_DEVICES_QUERY }],
        awaitRefetchQueries: true
      })
    );
  };

  disable = (): void => {
    this.listen(() =>
      this.props.mutate({
        variables: { value: 'disabled' },
        optimisticResponse: buildOptimisticMutationResponse(this.props, 'disabled'),
      })
    );
  };

  isInitialDataMissing(): boolean {
    return !idx(this.props.data, _ => _.me);
  }

  hasInitialDataErrored(): boolean {
    return !!idx(this.props.data, _ => _.error);
  }

  isPermissionDenied(): boolean {
    return !idx(this.props.data, _ => _.me.permissions.can_edit_phone_call_preferences);
  }

  getPreferenceValue(): ?string {
    return idx(this.props.data, _ => _.me.preferences.receive_phone_calls);
  }

  isSmsSupported(): ?boolean {
    return idx(this.props.data, _ => _.me.sms_supported);
  }

  render() {
    if (this.isInitialDataMissing()) {
      return null;
    }

    if (this.hasInitialDataErrored()) {
      return null;
    }

    if (this.isPermissionDenied()) {
      return null;
    }

    if (!this.isSmsSupported()) {
      return null;
    }

    const value = this.getPreferenceValue();

    if (!value) {
      return null;
    }

    return (
      <PhoneCallPreferenceSection
        data-test="phone-call-preference-section"
        value={value}
        enable={this.enable}
        disable={this.disable}
        persisted={this.state.persisted}
        persistError={this.state.persistError}
        smsSupported={this.props.smsSupported}
      />
    );
  }
}

const withPhoneCallPreferenceMutation = graphql(UPDATE_PREFERENCE_MUTATION);
const withPermission = graphql(GET_PERMISSION_QUERY);

export { PhoneCallPreferenceSectionContainer, setPreferredVoiceDevice };
export default withPermission(withPhoneCallPreferenceMutation(PhoneCallPreferenceSectionContainer));
